import {useMemo, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useReactToPrint} from 'react-to-print';
import {SVG} from '../../../../../constants/assetLists';
import {exportToExcelFromColumns} from '../../../../../utils/exportToExcel';
import AppMenu from '../../../../../components/common/AppMenu';
import AppConfirmationDialog from '../../../../../components/common/AppConfirmationDialog';
import AppPrintTable, {
  PrintColumn,
} from '../../../../../components/common/AppPrintTable';
import {useFormsContext} from '../../../../../context/forms/FormsContextProvider';
import {useClientsContext} from '../../../../../context/clients/ClientsContextProvider';
import {IForm} from '../../../forms';
import moment from 'moment';
import {formatNumber, getRoleTypeName} from '../../../../../utils';
import { FormsData } from '../../../../../services/operations/forms';

/**
 * Helper to create print columns
 */
const createFormColumns = (
  t: (key: string) => string,
  isHebrew: boolean,
): Array<PrintColumn<FormsData>> => [
  {
    title: t('forms.table.formName'),
    accessor: r => r.title || '-',
    width: 180,
  },
  {
    title: t('forms.table.creator'),
    accessor: r =>
      `${r.createdBy?.firstName ?? ''} ${r.createdBy?.lastName ?? ''}`.trim() ||
      '-',
    width: 150,
  },
  {
    title: t('forms.table.phone'),
    accessor: r => (r.createdBy?.phone ? formatNumber(r.createdBy.phone) : '-'),
    width: 150,
  },
  {
    title: t('forms.table.responses'),
    accessor: r => String(r.responsesCount || 0),
    width: 100,
  },
  {
    title: t('forms.table.creatorRole'),
    accessor: r => {
      const role = r.createdBy?.roles?.[0];
      return getRoleTypeName(role?.roleType, isHebrew ? 'he' : 'en') || '';
    },
    width: 150,
  },
  {
    title: t('forms.table.status'),
    accessor: r => {
      const statusLabels: Record<string, string> = {
        published: t('forms.table.statusPublished'),
        draft: t('forms.table.statusDraft'),
        archived: t('forms.table.statusArchived'),
      };
      return statusLabels[r.status ?? 'published'] ?? statusLabels.published;
    },
    width: 100,
  },
  {
    title: t('forms.table.organization'),
    accessor: r => r.clients?.map(c => c.name).join(', ') || '-',
    width: 180,
  },
  {
    title: t('forms.table.createdAt'),
    accessor: r => moment(r.createdAt).format('DD/MM/YYYY HH:mm'),
    width: 150,
  },
  {
    title: t('forms.table.updatedAt'),
    accessor: r => moment(r.updatedAt).format('DD/MM/YYYY HH:mm'),
    width: 150,
  },
  {
    title: t('forms.table.assignedTo'),
    accessor: r => {
      const recipientNames = r.recipients?.users?.map(rec => `${rec.firstName} ${rec.lastName}`) || [];
      const departmentNames = r.recipients?.departments?.map(d => d.name) || [];
      return [...recipientNames, ...departmentNames].join(', ') || '-';
    },
    width: 200,
  },
];

function FormsMenuSection() {
  const {t, i18n} = useTranslation();
  const isHebrew = i18n.language === 'heb';

  const {selectedForms, allForms, menuSelectedItem, setMenuSelectedItem} =
    useFormsContext();
  const {selectedLocation} = useClientsContext();

  const rawForms = allForms?.forms;

  // Create columns
  const formColumns = useMemo(
    () => createFormColumns(t, isHebrew),
    [t, isHebrew],
  );

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [showPrintDialog, setShowPrintDialog] = useState(false);

  const printRef = useRef<HTMLDivElement>(null);

  const rowsToPrint = useMemo(() => {
    if (!rawForms) return [];
    return selectedForms.length > 0
      ? rawForms.filter(f => selectedForms.includes(f._id))
      : rawForms;
  }, [rawForms, selectedForms]);

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: 'Forms',
  });

  /**
   * Menu items
   */
  const MENUITEMS = useMemo(
    () => [
      {
        title: t('forms.menu.export'),
        value: 'export',
        icon: SVG.csvGreenIcon,
      },
      {
        title: t('forms.menu.print'),
        value: 'print',
        icon: SVG.printerBlackIcon,
      },
      {
        title: t('forms.menu.showArchived'),
        value: 'archived',
        icon: 'checkbox',
      },
    ],
    [t],
  );

  const handleItemClick = (val: string) => {
    if (val === 'export') {
      const clientName =
        rowsToPrint && rowsToPrint.length > 0 && rowsToPrint[0]?.clients
          ? rowsToPrint[0]?.clients
              ?.map(c => c.name)
              .join(' ')
              ?.replaceAll(' ', '_')
          : 'Forms';
      const branchName =
        selectedLocation && !selectedLocation?.isClient
          ? selectedLocation?.name?.replaceAll(' ', '_')
          : null;
      const fileName = branchName
        ? `Forms_${clientName}_${branchName}.xlsx`
        : `Forms_${clientName}.xlsx`;
      exportToExcelFromColumns(formColumns, rowsToPrint ?? [], fileName);
    } else if (val === 'print') {
      setShowPrintDialog(true);
    } else if (val === 'archived') {
      setMenuSelectedItem(prev => {
        if (prev === 'archived') {
          return '';
        } else {
          return val;
        }
      });
    }

    setAnchorEl(null);
  };

  return (
    <>
      <AppMenu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onOpen={e => setAnchorEl(e.currentTarget as HTMLElement)}
        onClose={() => setAnchorEl(null)}
        items={MENUITEMS}
        showDivider
        dividerIndex={1}
        itemIconWidth={24}
        itemIconHeight={24}
        selectedItem={menuSelectedItem}
        onItemClick={handleItemClick}
      />

      <div style={{display: 'none'}}>
        <AppPrintTable<FormsData>
          ref={printRef}
          title={isHebrew ? 'רשימת טפסים' : 'Forms List'}
          subtitle={
            selectedForms.length > 0
              ? isHebrew
                ? `מספר טפסים מסומנים: ${selectedForms.length}`
                : `Selected forms: ${selectedForms.length}`
              : isHebrew
                ? `סה״כ טפסים: ${rowsToPrint.length}`
                : `Total forms: ${rowsToPrint.length}`
          }
          columns={formColumns}
          rows={rowsToPrint}
        />
      </div>

      <AppConfirmationDialog
        open={showPrintDialog}
        onClose={() => setShowPrintDialog(false)}
        onConfirm={() => {
          setShowPrintDialog(false);
          handlePrint();
        }}
        icon={
          <img
            src={SVG.printerAquaLgIcon}
            alt="icon"
            className="w-[48px] h-[48px] object-contain"
          />
        }
        title={isHebrew ? 'הדפסה' : 'Print'}
        subTitle={isHebrew ? 'שליחה להדפסה?' : 'Send to print?'}
        confirmBtnText={t('common.yes')}
        cancelBtnText={t('common.no')}
      />
    </>
  );
}

export default FormsMenuSection;
